import { VideoProject } from "@/services/bedrock";

import { KanbanColumnId } from "./kanbanColumns";

function completionTimestamp(project: VideoProject): number {
  const raw = project.qaDate || project.editedDate || project.updatedDate;
  const timestamp = raw ? Date.parse(raw) : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function hasQueueRole(project: VideoProject, userId: number, role: VideoProject.QueueAssignment["role"]): boolean {
  return (
    project.queueAssignments?.some(assignment => assignment.staffUserId === userId && assignment.role === role) ?? false
  );
}

type QueueRole = VideoProject.QueueAssignment["role"];

const COLUMN_ASSIGNMENT_ROLES: Partial<Record<KanbanColumnId, [QueueRole, QueueRole]>> = {
  submitted_for_editing: ["EDITOR", "QA"],
  editing: ["EDITOR", "QA"],
  submitted_for_qa_review: ["QA", "EDITOR"],
  qa_review: ["QA", "EDITOR"],
};

function isDirectAssignment(project: VideoProject, userId: number, role: QueueRole): boolean {
  return role === "EDITOR" ? project.editorUserId === userId : project.qaUserId === userId;
}

function statusQueueRole(project: VideoProject): QueueRole | null {
  if (project.status.endsWith("EDITING")) return "EDITOR";
  if (project.status.endsWith("QA_REVIEW")) return "QA";
  return null;
}

function kanbanQueuePriorityRank(project: VideoProject, columnId: KanbanColumnId, currentUserId: number): number {
  if (columnId === "completed") return 0;

  const roles = COLUMN_ASSIGNMENT_ROLES[columnId];
  if (!roles) return 4;

  const [primaryRole, secondaryRole] = roles;
  if (isDirectAssignment(project, currentUserId, primaryRole)) return 0;
  if (isDirectAssignment(project, currentUserId, secondaryRole)) return 1;
  if (hasQueueRole(project, currentUserId, primaryRole)) return 2;
  if (hasQueueRole(project, currentUserId, secondaryRole)) return 3;
  if (project.queueAssignments !== undefined) return 4;

  const priorityRole = project.isPriority ? statusQueueRole(project) : null;
  if (priorityRole === primaryRole) return 2;
  if (priorityRole === secondaryRole) return 3;
  return 4;
}

function compareKanbanQueueOrder(
  a: VideoProject,
  b: VideoProject,
  columnId: KanbanColumnId,
  currentUserId: number
): number {
  if (columnId === "completed") {
    const timeDiff = completionTimestamp(b) - completionTimestamp(a);
    if (timeDiff !== 0) return timeDiff;
    return b.id - a.id;
  }

  const rankDiff =
    kanbanQueuePriorityRank(a, columnId, currentUserId) - kanbanQueuePriorityRank(b, columnId, currentUserId);
  if (rankDiff !== 0) return rankDiff;
  return a.id - b.id;
}

export function insertProjectInKanbanQueueOrder(
  projects: VideoProject[],
  project: VideoProject,
  columnId: KanbanColumnId,
  currentUserId: number
): VideoProject[] {
  const others = projects.filter(item => item.id !== project.id);
  const index = others.findIndex(item => compareKanbanQueueOrder(project, item, columnId, currentUserId) < 0);
  if (index === -1) return [...others, project];
  return [...others.slice(0, index), project, ...others.slice(index)];
}

export function isPriorityFor(project: VideoProject, currentUserId: number): boolean {
  if (project.editorUserId === currentUserId || project.qaUserId === currentUserId) {
    return true;
  }

  const requiredRole = project.status.endsWith("EDITING")
    ? "EDITOR"
    : project.status.endsWith("QA_REVIEW")
    ? "QA"
    : null;
  if (!requiredRole) return false;

  return hasQueueRole(project, currentUserId, requiredRole);
}

export function buildOptimisticKanbanProjectPatch(
  project: VideoProject,
  args: {
    status: VideoProject.Status;
    event: VideoProject.Event;
    currentUserId: number;
    assignedUserId?: number;
  }
): Partial<VideoProject> {
  const now = new Date().toISOString();
  const assigneeId = args.assignedUserId ?? args.currentUserId;
  const patch: Partial<VideoProject> = {
    status: args.status,
    statusLabel: VideoProject.StatusMeta[args.status]?.label,
    statusDate: now,
    updatedDate: now,
  };

  switch (args.event) {
    case VideoProject.Event.START_EDITING:
      patch.editorUserId = project.editorUserId ?? assigneeId;
      break;
    case VideoProject.Event.START_QA_REVIEW:
      patch.qaUserId = project.qaUserId ?? assigneeId;
      break;
    case VideoProject.Event.COMPLETE:
    case VideoProject.Event.RESUBMIT:
      patch.editedDate = now;
      break;
    case VideoProject.Event.APPROVE_QA:
      patch.qaDate = now;
      patch.qaStatus = "APPROVED";
      break;
    case VideoProject.Event.REJECT_QA:
      patch.qaDate = now;
      patch.qaStatus = "REJECTED";
      patch.qaUserId = args.currentUserId;
      patch.requiresQa = true;
      break;
    case VideoProject.Event.REQUEST_REVISION:
      patch.revisionCount = (project.revisionCount ?? 0) + 1;
      break;
    default:
      break;
  }

  const nextProject = { ...project, ...patch };
  patch.isPriority = isPriorityFor(nextProject, args.currentUserId);
  return patch;
}
